import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { add as addToCart } from '../../Services/Cart.service';
import { setShowMiniCart } from '../../Actions/Cart.action';
import {
  receive as receiveCart,
  triggerAddToCartAnimation,
} from '../../Actions/Cart.action';
import { translate } from '../../Services/translation';
import QuantityInput from '../QuantityInput/QuantityInput';
import LoadingSpinner from '../LoadingSpinner';
import { useCallback } from 'react';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { setBuyButtonLoading } from '../../Actions/BuyButton.action';
import Icon from '../Icon';
import clsx from 'clsx';
import ExpressCheckoutButton from "../ExpressCheckout/ExpressCheckoutButton";
import { initExpressCheckoutFromCart, initExpressCheckoutFromProduct } from '../../utils/vippsUtils';
import { OrderRow } from '../../Types/OrderRow';

export interface BuyButtonAttributes {
  articleNumber?: string;
  disabled?: boolean;
  showQuantityInput?: boolean;
  onClick?: Function;
  className?: string;
  label?: string;
  quantity?: number;
  href?: string;
  hideText?: boolean;
  expressCheckoutEnabled?: boolean;
  clickSource?: string;
}

const BuyButton = (props: BuyButtonAttributes) => {
  const {
    articleNumber: localArticleNumber,
    showQuantityInput = true,
    className = '',
    onClick: customOnClick,
    label,
    quantity: initialQuantity,
    href,
    disabled: disabledProp,
    hideText = false,
    expressCheckoutEnabled = false,
    clickSource
  } = props;

  const {
    articleNumber: reducerArticleNumber,
    disabled = false,
    isLoading = false,
  } = useTypedSelector((state) => state.buyButton);

  const dispatch = useDispatch();
  const { current: userInfo } = useTypedSelector(
    (state) => state.userInfo
  );

  const {
    orderRows,
    expressCheckoutEnabled: cartExpressCheckoutEnabled,
  } = useTypedSelector((state) => state.cart);

  const [email] = useState<string | any>(userInfo?.email)

  const [quantity, setQuantity] = useState<number | undefined>(
    initialQuantity ?? 1
  );

  const [showLoadingSpinner, setShowLoadingSpinner] = useState<boolean>(
    false
  );
  const [errorText, setErrorText] = useState<string>('');

  const articleNumber = useMemo(
    () => localArticleNumber ?? reducerArticleNumber,
    [localArticleNumber, reducerArticleNumber]
  );

  const setIsLoading = (isLoading: boolean) => {
    dispatch(setBuyButtonLoading(isLoading));
  };

  const onClick = async () => {
    if (customOnClick) {
      customOnClick(articleNumber, quantity);
    } else if (!href) {
      setIsLoading(true);
      const loadingSpinnerTimeout = setTimeout(
        () => setShowLoadingSpinner(true),
        150
      );

      try {
        const cart = await addToCart({ articleNumber, quantity, email, clickSource });
        dispatch(receiveCart(cart));
        dispatch(triggerAddToCartAnimation());
      } catch (err) {
        setErrorText(translate('stock.not.enough'));
      } finally {
        clearTimeout(loadingSpinnerTimeout);
        setShowLoadingSpinner(false);
        setIsLoading(false);
      }
    }
  };


  const LoadingComponent = useCallback(
    () => (
      <>
        &nbsp;
        <span className="flex items-center justify-center absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 mx-auto [&_svg]:w-4 [&_svg]:h-4 [&_svg]:lg:w-6 [&_svg]:lg:h-6">
          <LoadingSpinner />
        </span>
      </>
    ),
    []
  );

  const _label = useMemo(
    () =>
      label ??
      (href
        ? translate('general.show')
        : translate('general.add-to-cart')),
    [href, label]
  );

  async function expressOnClick(): Promise<string> {

    if (orderRows && orderRows.length > 0) {
      dispatch(setShowMiniCart(true))
      return ''
    }
    else {
      return await initExpressCheckoutFromProduct(articleNumber ?? '', quantity ?? 0);
    }
  }

  return (
    <>
      <div className={clsx(className, 'flex gap-2')}>
        {showQuantityInput && (
          <QuantityInput
            quantity={quantity}
            showButtons={true}
            onChange={(value: number) => setQuantity(value)}
            className="flex-1"
          />
        )}
        {href && (
          <a
            className="w-full btn btn--primary btn--large flex gap-4 flex-grow self-stretch relative bg-primary-500 border border-solid border-primary-500 font-satoshi-bold font-satoshi hover:bg-primary-500 hover:border-primary-500 opacity-80 text-white"
            onClick={onClick}
            href={!disabled ? href : undefined}
          >
            {showLoadingSpinner && <LoadingComponent />}
            {!hideText && !showLoadingSpinner && (
              <>
                <Icon name="shopping-cart" className="!w-6 !h-6" />{' '}
                <span className="leading-none">{_label}</span>
              </>
            )}
            {hideText && !showLoadingSpinner && (
              <Icon name="shopping-cart" className="!w-6 !h-6" />
            )}
          </a>
        )}
        {!href && (
          <button
            onClick={onClick}
            className={clsx(
              !hideText && 'w-full',
              'btn btn--primary btn--large flex gap-4 flex-grow self-stretch relative bg-primary-500 border border-solid border-primary-500 font-satoshi-bold font-satoshi hover:bg-primary-500 hover:border-primary-500 opacity-80 text-white'
            )}
            disabled={
              disabled ||
              disabledProp ||
              isLoading ||
              !articleNumber ||
              (showQuantityInput && (!quantity || quantity <= 0))
            }
          >
            {showLoadingSpinner && <LoadingComponent />}
            {!hideText && !showLoadingSpinner && (
              <>
                <Icon name="shopping-cart" className="!w-6 !h-6" />{' '}
                <span className="leading-none">{_label}</span>
              </>
            )}
            {hideText && !showLoadingSpinner && (
              <Icon name="shopping-cart" className="!w-6 !h-6" />
            )}
          </button>
        )}
        {errorText && errorText.length > 0 && (
          <p className="text-error-500 absolute mt-12 text-sm">
            {errorText}
          </p>
        )}
      </div>

      {
        isExpressCheckoutEnabled(expressCheckoutEnabled, cartExpressCheckoutEnabled, orderRows) && (
          <ExpressCheckoutButton
            onClick={expressOnClick}
            variant='product'
          />
        )
      }
    </>
  );
};

export default BuyButton;

function isExpressCheckoutEnabled(expressCheckoutEnabled: boolean, cartExpressCheckoutEnabled: boolean, orderRows: OrderRow[]): boolean {

  if (orderRows && orderRows.length > 0) {
    return cartExpressCheckoutEnabled && expressCheckoutEnabled;
  }

  return expressCheckoutEnabled;
}
